type PortalRole = 'ADMIN' | 'OPERATIONS' | 'SALES' | 'GM' | 'CLIENT';

interface PortalRedirectInput {
  pathname: string;
  isAuthenticated: boolean;
  hasRole: (role: PortalRole) => boolean;
}

const PORTAL_ROLE_BY_PREFIX: ReadonlyArray<[string, PortalRole]> = [
  ['/admin', 'ADMIN'],
  ['/operations', 'OPERATIONS'],
  ['/sales', 'SALES'],
  ['/gm', 'GM'],
  ['/client', 'CLIENT'],
];

// Bare prefixes that need an explicit forward to a sub-page; portals whose index
// route already renders a dashboard are NOT listed here (TanStack matches the
// index route directly and we must not bounce the user off it).
const BARE_PREFIX_FORWARD: Record<string, string> = {
  '/client': '/client/dashboard',
};

/**
 * Returns the redirect target for any portal-prefixed path the user shouldn't see,
 * or null if the current path is fine for the current user.
 *
 * Acts as a backstop to TanStack route-level guards: even if a guard fails to paint
 * a redirect, this effect-level check in the root layout still routes cross-role
 * attempts to /unauthorized.
 */
export function getPortalRedirectTarget({
  pathname,
  isAuthenticated,
  hasRole,
}: PortalRedirectInput): string | null {
  for (const [prefix, role] of PORTAL_ROLE_BY_PREFIX) {
    const matches = pathname === prefix || pathname.startsWith(`${prefix}/`);
    if (!matches) continue;

    if (!isAuthenticated) return '/login';
    if (!hasRole(role)) return '/unauthorized';

    // Same-role at the bare prefix: only forward when there's no portal index
    // route to render. Otherwise return null and let the index route handle it.
    if (pathname === prefix && BARE_PREFIX_FORWARD[prefix]) {
      return BARE_PREFIX_FORWARD[prefix];
    }
    return null;
  }
  return null;
}
